import { memo, type ForwardedRef } from 'react';
import includes from 'lodash/includes';
import map from 'lodash/map';
import { useIntl } from 'react-intl';
// Material UI imports
import type { ButtonProps } from '@mui/material';
import ButtonGroup, { type ButtonGroupProps } from '@mui/material/ButtonGroup';
// local imports
import { simpleForwardRef } from '../helpers/react';
import ScopeSelectorButton from './ScopeSelectorButton';
// SCSS imports
import { button } from './ScopeSelector.module.scss';

type ScopeSelectorProps<T> = {
  readonly scope: readonly T[];
  readonly unavailable?: readonly T[];
  readonly value: T;
  readonly onChange: (item: T) => void;
  readonly disabled?: boolean
  readonly simple?: boolean;
  readonly ariaLabel?: string;
  readonly sx?: ButtonGroupProps['sx'];
  readonly buttonSx?: ButtonProps['sx'];
};

const ScopeSelectorRender = <T extends string>({
  scope,
  unavailable,
  value,
  onChange,
  disabled = false,
  simple = false,
  ariaLabel,
  sx,
  buttonSx
}: ScopeSelectorProps<T>, ref: ForwardedRef<HTMLDivElement>) => {
  const { formatMessage } = useIntl();
  return (
    <ButtonGroup
        ref={ref}
        color="primary"
        size="small"
        disableElevation
        disableRipple
        disabled={disabled}
        aria-label={formatMessage(ariaLabel ? { id: ariaLabel, defaultMessage: ariaLabel } : { id: 'common.scope.label' })}
        sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, ...sx || {} }}
    >
      {map(scope, (item) => (
        <ScopeSelectorButton
            key={item}
            sx={buttonSx}
            item={item}
            selected={item === value}
            simple={simple}
            disabled={unavailable ? includes(unavailable, item) : undefined}
            onChange={onChange as ((item: string) => void)}
            className={button}
        />
      ))}
    </ButtonGroup>
  );
};

const ScopeSelector = simpleForwardRef(ScopeSelectorRender);

// ScopeSelector.displayName = 'ScopeSelector';

export default memo(ScopeSelector);
